import React from 'react';
import { useI18n } from '../../context/I18nContext';
import Button from './Button';
import ExportImportButtons from './ExportImportButtons';
import { Plus } from 'lucide-react';

/**
 * Configuration pour les actions d'une table
 */
export interface TableActionConfig {
  /** Clé de traduction pour le bouton "New" */
  newButtonKey?: string;
  /** Callback pour créer un nouvel élément */
  onCreateNew?: () => void;
  /** Callback pour l'export */
  onExport?: () => void;
  /** Callback pour l'import */
  onImport?: () => void;
  /** Icône personnalisée pour le bouton "New" */
  newButtonIcon?: React.ReactNode;
  /** Variante du bouton "New" */
  newButtonVariant?: 'primary' | 'secondary' | 'outline';
  /** Actions personnalisées supplémentaires */
  customActions?: React.ReactNode;
}

/**
 * Props pour le composant TableActions
 */
interface TableActionsProps {
  /** Configuration des actions */
  config: TableActionConfig;
  /** Classe CSS supplémentaire */
  className?: string;
}

/**
 * Composant pour afficher les actions d'une table (New, Export, Import, etc.)
 * 
 * @example
 * ```tsx
 * <TableActions
 *   config={{
 *     newButtonKey: 'leads.createLead',
 *     onCreateNew: handleCreateLead,
 *     onExport: handleExport,
 *     onImport: handleImport,
 *     newButtonIcon: <Plus className="w-4 h-4" />
 *   }}
 * />
 * ```
 */
const TableActions: React.FC<TableActionsProps> = ({
  config,
  className = ''
}) => {
  const { t } = useI18n();

  const {
    newButtonKey,
    onCreateNew,
    onExport,
    onImport,
    newButtonIcon = <Plus className="w-4 h-4" />,
    newButtonVariant = 'primary',
    customActions
  } = config;

  return (
    <div className={`flex flex-col sm:flex-row gap-3 justify-between items-start sm:items-center ${className}`}>
      {/* Actions principales (New, Export, Import) */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Bouton "New" */}
        {newButtonKey && onCreateNew && (
          <Button
            variant={newButtonVariant}
            onClick={onCreateNew}
            icon={newButtonIcon}
            className="flex-shrink-0"
          >
            {t(newButtonKey)}
          </Button>
        )}

        {/* Export/Import — hidden on mobile */}
        <ExportImportButtons
          onExport={onExport}
          onImport={onImport}
          className="hidden sm:flex flex-row gap-2"
        />
      </div>

      {/* Actions personnalisées (à droite) */}
      {customActions && (
        <div className="flex gap-2">
          {customActions}
        </div>
      )}
    </div>
  );
};

export default TableActions;
